import { useTranslation } from 'react-i18next';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { getProviderPrimarySpecialty } from '@provider-directory/models/provider.model';
import { ProviderResource } from '@provider-directory/types/providers.contracts';

interface MyChartRedirect {
  myChartUrl: URL | undefined;
}

/** Malformed URLs already reported; the hook runs on every card render. */
const warnedMalformedUrls = new Set<string>();

/**
 * `undefined` when `myChartUrl` is not a parseable absolute URL. Both sources
 * are supposed to guarantee one — the API schema requires an absolute URL and
 * the legacy config is code-reviewed — but this hook runs on every provider-card
 * render, so a bad value must cost the MyChart link, not the whole page.
 *
 * Reaching the `catch` means misconfiguration, not opt-out: opting out is
 * handled before the call, so it stays silent.
 */
const buildMyChartUrl = (
  myChartUrl: string,
  providerId: string,
  visitTypeId: string,
  lang: string,
): URL | undefined => {
  let url: URL;
  try {
    url = new URL(myChartUrl);
  } catch {
    if (!warnedMalformedUrls.has(myChartUrl)) {
      warnedMalformedUrls.add(myChartUrl);
      console.warn(
        `[useMyChartRedirect] MyChart url is not a valid absolute URL; no deep link offered: "${myChartUrl}"`,
      );
    }
    return undefined;
  }
  url.searchParams.set('lang', lang);
  url.searchParams.set('id', providerId);
  url.searchParams.set('vt', visitTypeId);
  return url;
};

const useMyChartRedirect = (provider: ProviderResource): MyChartRedirect => {
  const orgContext = useOrgContext();
  const { i18n } = useTranslation();

  // Capability resolution is centralized in OrgContextModel: the hook receives
  // already-resolved MyChart inputs and never inspects the tenant key, feature
  // flag, or runtime config.
  const resolution = orgContext.getMyChartVisitTypeConfig(provider);

  // No MyChart for this directory — nothing to deep-link to, and returning
  // `undefined` keeps callers from opening `about:blank`. Covers both "no
  // `integration.myChart` at all" and "no URL for this environment". Opting out
  // is a normal state and must not log.
  if (resolution === undefined) {
    return { myChartUrl: undefined };
  }

  const { url, defaultVisitTypeId, specialtyToVisitTypeId } = resolution;

  const specialty =
    getProviderPrimarySpecialty(provider)?.name.toLowerCase() ?? '';
  const visitTypeId = specialtyToVisitTypeId[specialty];

  return {
    myChartUrl: buildMyChartUrl(
      url,
      provider.ehrExternalId!,
      visitTypeId ?? defaultVisitTypeId,
      i18n.language,
    ),
  };
};

export default useMyChartRedirect;
